import {
  CartItem,
  CartItemOptionItems,
  OptionConfiguration,
  ProductOptionItem,
} from '@pedix-workspace/utils';
import { ApiV1_Category, ApiV1_Product, ApiV1_ProductOptionItem } from '@pedix-workspace/api-v1';
import { createOrUpdateCartItem } from './cart-item-utils';
import { countSelectedOptionItems, getOptionSelectionRange } from './option-selection-range';

export interface RehydratedCartItems {
  updatedCartItems: CartItem[];
  removedCartItems: CartItem[];
}

export function rehydrateCartItemsAgainstCatalog(
  cartItems: CartItem[],
  categories: ApiV1_Category[],
  establishmentId: string,
): RehydratedCartItems {
  const updatedCartItems: CartItem[] = [];
  const removedCartItems: CartItem[] = [];

  for (const cartItem of cartItems) {
    const rehydratedCartItem = rehydrateCartItem(cartItem, categories, establishmentId);

    if (rehydratedCartItem) {
      updatedCartItems.push(rehydratedCartItem);
    } else {
      removedCartItems.push(cartItem);
    }
  }

  return { updatedCartItems, removedCartItems };
}

function rehydrateCartItem(
  cartItem: CartItem,
  categories: ApiV1_Category[],
  establishmentId: string,
): CartItem | null {
  const sourceCategoryId = cartItem.category.clonedFrom || cartItem.category.id;
  const matchingCategory = categories.find(
    category => sourceCategoryId === (category.clonedFrom || category.id),
  );

  if (!matchingCategory || !matchingCategory.active) {
    return null;
  }

  const sourceProductId = cartItem.product.clonedFrom || cartItem.product.id;
  const matchingProduct = matchingCategory.products.find(
    product => sourceProductId === (product.clonedFrom || product.id),
  );

  if (!matchingProduct || !matchingProduct.active) {
    return null;
  }

  const migratedPresentation = migratePresentation(cartItem, matchingProduct);
  const migratedOptions = migrateOptions(cartItem, matchingProduct);

  if (
    migratedPresentation === DISCARD ||
    migratedOptions === DISCARD ||
    !hasRequiredSelections(matchingProduct, migratedPresentation, migratedOptions)
  ) {
    return null;
  }

  return createOrUpdateCartItem({
    category: matchingCategory,
    product: matchingProduct,
    establishmentId,
    cartItemId: cartItem.id,
    selectedPresentationOption: migratedPresentation,
    productQuantity: cartItem.qty,
    productObservations: cartItem.observations,
    selectedOptions: migratedOptions,
  });
}

const DISCARD = Symbol('discard-cart-item');

function hasRequiredSelections(
  product: ApiV1_Product,
  presentation: CartItemOptionItems | undefined,
  options: CartItemOptionItems[],
): boolean {
  if (product.presentations && !presentation?.items.length) {
    return false;
  }

  return (product.options ?? []).every(option => {
    const requiredCount = getOptionSelectionRange(option).min;

    if (!requiredCount) {
      return true;
    }

    const selectedItems = options.find(selected => selected.option.id === option.id)?.items ?? [];

    return countSelectedOptionItems(selectedItems) >= requiredCount;
  });
}

function migrateOptionItems(
  sourceItems: ProductOptionItem[],
  targetItems: ApiV1_ProductOptionItem[],
): ApiV1_ProductOptionItem[] | typeof DISCARD {
  const migrated = sourceItems
    .map(sourceItem => {
      const sourceItemId = sourceItem.clonedFrom || sourceItem.id;
      const matchingItem = targetItems.find(item => sourceItemId === (item.clonedFrom || item.id));

      return matchingItem && !matchingItem.hidden
        ? <ApiV1_ProductOptionItem>{ ...matchingItem, quantity: sourceItem.quantity }
        : null;
    })
    .filter((item): item is ApiV1_ProductOptionItem => !!item);

  return migrated.length === sourceItems.length ? migrated : DISCARD;
}

function migratePresentation(
  cartItem: CartItem,
  matchingProduct: ApiV1_Product,
): CartItemOptionItems | undefined | typeof DISCARD {
  if (!cartItem.selectedPresentation || !matchingProduct.presentations) {
    return undefined;
  }

  const sourcePresentationId =
    cartItem.selectedPresentation.option.clonedFrom || cartItem.selectedPresentation.option.id;
  const targetPresentationId =
    matchingProduct.presentations.clonedFrom || matchingProduct.presentations.id;

  if (sourcePresentationId !== targetPresentationId) {
    return DISCARD;
  }

  const migratedItems = migrateOptionItems(
    cartItem.selectedPresentation.items,
    matchingProduct.presentations.items,
  );

  if (migratedItems === DISCARD) {
    return DISCARD;
  }

  return {
    option: { ...matchingProduct.presentations, items: [] },
    items: migratedItems,
  } as CartItemOptionItems;
}

function migrateOptions(
  cartItem: CartItem,
  matchingProduct: ApiV1_Product,
): CartItemOptionItems[] | typeof DISCARD {
  if (!Array.isArray(cartItem.selectedOptions) || cartItem.selectedOptions.length === 0) {
    return [];
  }

  if (!Array.isArray(matchingProduct.options)) {
    return DISCARD;
  }

  const migrated = cartItem.selectedOptions
    .map(selectedOption => {
      const sourceOptionId = selectedOption.option.clonedFrom || selectedOption.option.id;
      const matchingOption = matchingProduct.options.find(
        option => sourceOptionId === (option.clonedFrom || option.id),
      );

      if (!matchingOption) {
        return null;
      }

      const migratedItems = migrateOptionItems(selectedOption.items, matchingOption.items);

      if (migratedItems === DISCARD) {
        return null;
      }

      return {
        option: { ...matchingOption, items: [] } as OptionConfiguration,
        items: migratedItems as ProductOptionItem[],
      };
    })
    .filter((option): option is CartItemOptionItems => option !== null);

  return migrated.length === cartItem.selectedOptions.length ? migrated : DISCARD;
}
